// Persist active workout session context + per-runner UI state to localStorage,
// so navigating away from /client/training (e.g. to answer a phone call) does
// not wipe in-progress reps/weights, the elapsed timer, or which sets have been
// ticked off in the current session.
//
// Extended in Phase 1/2 of Training Engine Resilience to also handle:
//   - Full exercise list caching (offline fallback)
//   - Offline mutation queue (logSet / complete)
//   - Workout meta for the floating ActiveWorkoutPill

const ACTIVE_KEY = 'workout-session:active';
const RUNNER_KEY = (workoutId: number) => `workout-session:runner:${workoutId}`;
const EXERCISES_KEY = (templateId: number) => `workout-session:exercises:${templateId}`;
const META_KEY = 'workout-session:meta';
const MUTATION_QUEUE_KEY = 'workout-session:mutation-queue';

// ─── Session Identity ──────────────────────────────────────────────────────────

export type WorkoutMode = 'select' | 'standard' | 'guided';

export interface ActiveWorkoutSession {
  workoutId: number;
  templateId: number;
  mode: WorkoutMode;
}

export function loadActiveSession(): ActiveWorkoutSession | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.workoutId !== 'number' || typeof parsed?.templateId !== 'number') return null;
    return parsed as ActiveWorkoutSession;
  } catch {
    return null;
  }
}

export function saveActiveSession(s: ActiveWorkoutSession | null) {
  try {
    if (s) localStorage.setItem(ACTIVE_KEY, JSON.stringify(s));
    else localStorage.removeItem(ACTIVE_KEY);
  } catch {
    // localStorage may be unavailable (private mode, quota); fail silently.
  }
}

// ─── Preferred Runner Mode ────────────────────────────────────────────────────
// The last runner view the member chose (Standard or Guided). The Training
// hero toggle starts from it and writes it back whenever it changes.

const PREFERRED_MODE_KEY = 'workout-session:preferred-mode';

export type PreferredWorkoutMode = 'standard' | 'guided';

export function loadPreferredMode(fallback: PreferredWorkoutMode = 'standard'): PreferredWorkoutMode {
  try {
    const raw = localStorage.getItem(PREFERRED_MODE_KEY);
    return raw === 'standard' || raw === 'guided' ? raw : fallback;
  } catch {
    return fallback;
  }
}

export function savePreferredMode(mode: PreferredWorkoutMode) {
  try {
    localStorage.setItem(PREFERRED_MODE_KEY, mode);
  } catch {
    // ignore (quota, private mode)
  }
}

// ─── Runner State (full set logs, block indices, etc.) ─────────────────────────

export function loadRunnerState<T>(workoutId: number): T | null {
  try {
    const raw = localStorage.getItem(RUNNER_KEY(workoutId));
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function saveRunnerState<T>(workoutId: number, state: T) {
  try {
    localStorage.setItem(RUNNER_KEY(workoutId), JSON.stringify(state));
  } catch {
    // ignore (quota, private mode)
  }
}

export function clearRunnerState(workoutId: number) {
  try {
    localStorage.removeItem(RUNNER_KEY(workoutId));
  } catch {
    // ignore
  }
}

// ─── Workout Meta (for the floating pill) ──────────────────────────────────────
// Lightweight metadata so the ActiveWorkoutPill can display the workout name
// and elapsed time without loading the full runner state blob.

export interface WorkoutMeta {
  workoutName: string;
  sessionStart: number; // Date.now() when the session began
}

export function saveWorkoutMeta(meta: WorkoutMeta) {
  try {
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch {
    // ignore
  }
}

export function loadWorkoutMeta(): WorkoutMeta | null {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.workoutName !== 'string' || typeof parsed?.sessionStart !== 'number') return null;
    return parsed as WorkoutMeta;
  } catch {
    return null;
  }
}

export function clearWorkoutMeta() {
  try {
    localStorage.removeItem(META_KEY);
  } catch {
    // ignore
  }
}

// ─── Exercise Cache (offline fallback) ─────────────────────────────────────────
// On first API load the runner caches the exercise list so that if the user
// loses connectivity mid-session (or opens the app offline), the exercises are
// still available.

export function saveCachedExercises<T>(templateId: number, exercises: T[]) {
  try {
    localStorage.setItem(EXERCISES_KEY(templateId), JSON.stringify({
      exercises,
      cachedAt: Date.now(),
    }));
  } catch {
    // ignore
  }
}

export function loadCachedExercises<T>(templateId: number, maxAgeMs = 48 * 60 * 60 * 1000): T[] | null {
  try {
    const raw = localStorage.getItem(EXERCISES_KEY(templateId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.exercises)) return null;
    // Expire cache after maxAgeMs (default 48h)
    if (typeof parsed.cachedAt === 'number' && Date.now() - parsed.cachedAt > maxAgeMs) {
      localStorage.removeItem(EXERCISES_KEY(templateId));
      return null;
    }
    return parsed.exercises as T[];
  } catch {
    return null;
  }
}

export function clearCachedExercises(templateId: number) {
  try {
    localStorage.removeItem(EXERCISES_KEY(templateId));
  } catch {
    // ignore
  }
}

// ─── Offline Mutation Queue ────────────────────────────────────────────────────
// When the client is offline, tRPC mutations (logSet, completeWorkout) can't
// fire. Instead of silently failing, we buffer them here and drain the queue
// when connectivity returns (handled by the useOfflineSync hook).

export type QueuedMutationType = 'logSet' | 'completeWorkout';

export interface QueuedMutation {
  id: string;         // unique ID for dedup
  type: QueuedMutationType;
  payload: unknown;
  queuedAt: number;   // Date.now()
}

export function getMutationQueue(): QueuedMutation[] {
  try {
    const raw = localStorage.getItem(MUTATION_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function queueMutation(type: QueuedMutationType, payload: unknown): QueuedMutation | null {
  try {
    const queue = getMutationQueue();
    const mutation: QueuedMutation = {
      id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      payload,
      queuedAt: Date.now(),
    };
    queue.push(mutation);
    localStorage.setItem(MUTATION_QUEUE_KEY, JSON.stringify(queue));
    return mutation;
  } catch {
    return null;
  }
}

export function removeMutationFromQueue(id: string) {
  try {
    const queue = getMutationQueue().filter(m => m.id !== id);
    localStorage.setItem(MUTATION_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // ignore
  }
}

export function clearMutationQueue() {
  try {
    localStorage.removeItem(MUTATION_QUEUE_KEY);
  } catch {
    // ignore
  }
}

export function getMutationQueueSize(): number {
  return getMutationQueue().length;
}

// ─── Full Session Cleanup ──────────────────────────────────────────────────────
// Called when a workout is completed or cancelled. Clears everything.

export function clearFullSession(workoutId: number, templateId: number) {
  clearRunnerState(workoutId);
  clearCachedExercises(workoutId);
  clearCachedExercises(templateId);
  clearWorkoutMeta();
  saveActiveSession(null);
  // Note: we do NOT clear the mutation queue here — unsynced mutations
  // should persist until they're successfully drained.
}
